import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { AdminFeaturesService } from '../service/admin-features.service';

type Provider = 'resend' | 'sendpository';
type Tab = 'settings' | 'activity';

const LABEL: Record<Provider, string> = { resend: 'Resend', sendpository: 'Sendpository' };

/** Every delivery state Sendpository reports, in a sensible display order. */
const STATUSES = [
  'delivered', 'sent', 'queued', 'scheduled',
  'delayed', 'bounced', 'complained', 'failed', 'cancelled',
] as const;
type MailStatus = typeof STATUSES[number];

interface ActivityRow {
  id: string; to: string[]; subject: string; status: string; createdAt: string;
}
interface ActivityDetail extends ActivityRow {
  from: string; sentAt: string | null; deliveredAt: string | null;
  tags: Record<string, string> | null;
  events: { type: string; at: string }[];
}

@Component({
  selector: 'app-mail-settings',
  templateUrl: './mail-settings.component.html',
  styleUrls: ['./mail-settings.component.css'],
  providers: [MessageService],
})
export class MailSettingsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  tab: Tab = 'settings';

  loading = true;
  saving = false;
  active: Provider | null = null;
  configured: Record<Provider, boolean> = { resend: false, sendpository: false };
  /** The radio selection, which may differ from `active` until Save is clicked. */
  selected: Provider | null = null;

  /** Independent of `selected` above - lets an admin test a provider without switching to it. */
  testProvider: Provider = 'resend';
  testTo = '';
  testing = false;
  testResult: { ok: boolean; provider: Provider; error?: string } | null = null;

  // ── Email Activity ─────────────────────────────────────────────────────────
  statuses = STATUSES;
  activityFilter: MailStatus | '' = '';
  activityLoading = false;
  activityError = '';
  activity: ActivityRow[] = [];
  /**
   * Fixed overview of the last 150 sends, computed ONLY from the unfiltered
   * load. The tiles double as filter buttons for the table below, but their
   * numbers must not move when a filter is on - a "3 bounced" tile that
   * becomes "15 bounced" the moment you click it is worse than useless.
   */
  counts: Record<string, number> = {};
  overviewTotal = 0;
  selectedEmail: ActivityDetail | null = null;
  detailLoading = false;

  label = LABEL;
  providers: Provider[] = ['resend', 'sendpository'];

  constructor(private svc: AdminFeaturesService, private toast: MessageService) {}

  ngOnInit() { this.load(); }
  ngOnDestroy() { this.destroy$.next(); this.destroy$.complete(); }

  setTab(t: Tab) {
    this.tab = t;
    if (t === 'activity' && !this.overviewTotal && !this.activityError) this.refreshActivity();
  }

  load() {
    this.loading = true;
    this.svc.mailSettings().pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.active = r.active;
        this.selected = r.active;
        const providers = r.providers || {};
        this.configured = {
          resend:      !!(providers.resend && providers.resend.configured),
          sendpository: !!(providers.sendpository && providers.sendpository.configured),
        };
        // Default the test form to a configured provider, not necessarily the active one.
        this.testProvider = this.configured[r.active] ? r.active : (this.configured.resend ? 'resend' : 'sendpository');
        this.loading = false;
      },
      () => { this.loading = false; this.err('Could not load mail settings'); },
    );
  }

  get dirty(): boolean { return !!this.selected && this.selected !== this.active; }

  save() {
    if (!this.selected || !this.dirty) return;
    if (!this.configured[this.selected]) {
      this.err(`${this.label[this.selected]} has no API key configured on the server yet.`);
      return;
    }
    this.saving = true;
    this.svc.setMailProvider(this.selected).pipe(takeUntil(this.destroy$)).subscribe(
      () => {
        this.saving = false;
        this.active = this.selected;
        this.ok(`${this.label[this.selected!]} is now the active provider. Every OTP, reset link and receipt goes through it from now on.`);
      },
      (e: any) => { this.saving = false; this.err((e && e.error && e.error.error) || 'Could not switch provider'); },
    );
  }

  runTest() {
    const to = this.testTo.trim();
    if (!to) { this.err('Enter an address to send the test to'); return; }
    this.testing = true;
    this.testResult = null;
    this.svc.testMail(to, this.testProvider).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        this.testing = false;
        this.testResult = r;
        if (r.ok) this.ok(`Test email sent via ${this.label[this.testProvider]}. Check ${to}.`);
        else this.err(r.error || 'Send failed');
      },
      (e: any) => {
        this.testing = false;
        const msg = (e && e.error && e.error.error) || 'Send failed';
        this.testResult = { ok: false, provider: this.testProvider, error: msg };
        this.err(msg);
      },
    );
  }

  // ── Email Activity ─────────────────────────────────────────────────────────

  /** Reloads BOTH the fixed overview tiles and the (possibly filtered) table. */
  refreshActivity() {
    if (!this.configured.sendpository) {
      this.activityError = 'Email activity is only available for Sendpository. Add SENDPOSITORY_API_KEY on the server first.';
      return;
    }
    this.activityError = '';
    // Tiles: always the unfiltered last-150 window.
    this.svc.mailActivity().pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => {
        const rows: ActivityRow[] = r.emails || [];
        this.overviewTotal = rows.length;
        this.counts = {};
        for (const e of rows) this.counts[e.status] = (this.counts[e.status] || 0) + 1;
      },
      () => { /* the table load below surfaces the error */ },
    );
    this.loadTable();
  }

  /** Just the table rows, honouring the current status filter. */
  loadTable() {
    this.activityLoading = true;
    this.svc.mailActivity(this.activityFilter || undefined).pipe(takeUntil(this.destroy$)).subscribe(
      (r: any) => { this.activity = r.emails || []; this.activityLoading = false; },
      (e: any) => {
        this.activityLoading = false;
        this.activityError = (e && e.error && e.error.error) || 'Could not load email activity';
      },
    );
  }

  filterBy(s: MailStatus | '') {
    if (this.activityFilter === s) return;
    this.activityFilter = s;
    this.loadTable();
  }

  openEmail(row: ActivityRow) {
    this.selectedEmail = { ...row, from: '', sentAt: null, deliveredAt: null, tags: null, events: [] };
    this.detailLoading = true;
    this.svc.mailActivityDetail(row.id).pipe(takeUntil(this.destroy$)).subscribe(
      (d: any) => { this.selectedEmail = d; this.detailLoading = false; },
      () => { this.detailLoading = false; this.err('Could not load that email'); },
    );
  }
  closeEmail() { this.selectedEmail = null; }

  /** Status → badge tone. The three that mean something is wrong stand out. */
  statusTone(s: string): 'ok' | 'warn' | 'bad' | 'mute' {
    if (s === 'delivered' || s === 'sent') return 'ok';
    if (s === 'delayed' || s === 'queued' || s === 'scheduled') return 'warn';
    if (s === 'bounced' || s === 'complained' || s === 'failed') return 'bad';
    return 'mute';
  }

  fmtTime(iso: string | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  }

  tagList(tags: Record<string, string> | null): string {
    if (!tags) return '';
    return Object.entries(tags).map(([k, v]) => `${k}: ${v}`).join(' · ');
  }

  private ok(m: string)  { this.toast.add({ key: 'custom', severity: 'success', summary: m, life: 8000 }); }
  private err(m: string) { this.toast.add({ key: 'custom', severity: 'error', summary: m, life: 8000 }); }
}
